
import React, { useState } from 'react';
import { useAiAssistant } from '@/contexts/ai-assistant/AIAssistantContext';
import { AiTitleSuggestion } from './types';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Loader2, RefreshCw, ThumbsUp, ThumbsDown, AlertCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface TitleGeneratorProps {
  content: string;
  currentTitle: string;
  onSelectTitle: (title: string) => void;
  onClose: () => void;
}

const TitleGenerator: React.FC<TitleGeneratorProps> = ({
  content,
  currentTitle,
  onSelectTitle,
  onClose
}) => {
  const { generateTitles, isProcessing } = useAiAssistant();
  const [suggestions, setSuggestions] = useState<AiTitleSuggestion[]>([]);
  const [selectedTitle, setSelectedTitle] = useState<string | null>(null);
  const [feedbackGiven, setFeedbackGiven] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);

  const handleGenerateTitles = async () => {
    setError(null);
    
    if (!content || content.trim().length === 0) {
      setError("Cannot generate titles for empty content. Please add some text to your article first.");
      return;
    }
    
    // Use a sample of content to reduce payload size
    const contentSample = content.length > 3000 
      ? content.substring(0, 3000) + "..." 
      : content;
      
    const response = await generateTitles(contentSample, currentTitle);
    
    if (response.success && response.data) {
      try {
        console.log("Response data type:", typeof response.data);
        console.log("Raw response data:", JSON.stringify(response.data));
        
        // Handle different response formats
        let processedData;
        
        if (Array.isArray(response.data)) {
          processedData = response.data;
        } else if (typeof response.data === 'object') {
          // It might be an object with titles property
          if (response.data.titles && Array.isArray(response.data.titles)) {
            processedData = response.data.titles;
          } else {
            // Try to process the object directly
            processedData = Object.values(response.data).filter(item => 
              typeof item === 'object' && item !== null && 'title' in item && 'style' in item
            );
          }
        } else if (typeof response.data === 'string') {
          // Try to parse string response
          try {
            const parsed = JSON.parse(response.data);
            if (Array.isArray(parsed)) {
              processedData = parsed;
            } else if (parsed.titles && Array.isArray(parsed.titles)) {
              processedData = parsed.titles;
            } else {
              processedData = [];
            }
          } catch (parseError) {
            console.error("Error parsing string data:", parseError);
            processedData = [];
          }
        }
        
        if (Array.isArray(processedData) && processedData.length > 0) {
          setSuggestions(processedData);
        } else {
          console.error("Could not process data into expected format:", processedData);
          setError("Failed to parse AI response into title suggestions. Please try again.");
        }
      } catch (err) {
        console.error("Error processing title suggestions:", err, "Response data:", response.data);
        setError("Error processing AI response. Please try again.");
      }
    } else {
      setError(response.error || "Failed to generate titles. Please try again.");
    }
  };

  const handleTitleSelect = (title: string) => {
    setSelectedTitle(title);
  };

  const handleApplyTitle = () => {
    if (selectedTitle) {
      onSelectTitle(selectedTitle);
      onClose();
    }
  };

  const handleFeedback = (title: string, positive: boolean) => {
    setFeedbackGiven(prev => ({
      ...prev,
      [title]: positive
    }));
    // In a real implementation, we would send this feedback to the server
  };

  const getTitleCharacterCount = (title: string) => {
    return title.length;
  };

  const getColorForLength = (length: number) => {
    if (length <= 50) return "text-green-500";
    if (length <= 60) return "text-yellow-500";
    return "text-red-500";
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>AI Title Generator</CardTitle>
        <CardDescription>
          Generate compelling headlines that will engage readers and optimize for SEO
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        {!suggestions.length && !isProcessing && !error ? (
          <div className="flex flex-col items-center justify-center p-8">
            <p className="text-muted-foreground mb-4 text-center">
              Our AI will analyze your article and suggest compelling titles that capture its essence.
            </p>
            <Button onClick={handleGenerateTitles}>
              Generate Title Suggestions
            </Button>
          </div>
        ) : isProcessing ? (
          <div className="flex flex-col items-center justify-center p-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
            <p className="text-muted-foreground">Analyzing your article and generating titles...</p>
          </div>
        ) : (
          <>
            <div className="grid gap-3">
              {suggestions.map((suggestion, index) => (
                <div 
                  key={index}
                  className={`p-3 rounded-md border cursor-pointer transition-colors ${
                    selectedTitle === suggestion.title 
                      ? 'bg-primary/10 border-primary' 
                      : 'hover:bg-accent'
                  }`}
                  onClick={() => handleTitleSelect(suggestion.title)}
                >
                  <div className="flex justify-between items-start mb-1">
                    <p className="font-medium">{suggestion.title}</p>
                    <div className="flex items-center gap-1 ml-2">
                      {feedbackGiven[suggestion.title] === undefined ? (
                        <>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-7 w-7"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleFeedback(suggestion.title, true);
                            }}
                          >
                            <ThumbsUp className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-7 w-7"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleFeedback(suggestion.title, false);
                            }}
                          >
                            <ThumbsDown className="h-4 w-4" />
                          </Button>
                        </>
                      ) : (
                        <Badge variant="outline" className="text-xs">
                          Feedback recorded
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    <Badge variant="secondary">{suggestion.style}</Badge>
                    <span 
                      className={`text-xs ${getColorForLength(getTitleCharacterCount(suggestion.title))}`}
                    >
                      {getTitleCharacterCount(suggestion.title)} characters
                    </span>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="flex justify-center mt-4">
              <Button 
                variant="outline" 
                size="sm" 
                className="flex items-center gap-1"
                onClick={handleGenerateTitles}
                disabled={isProcessing}
              >
                <RefreshCw className="h-4 w-4" /> 
                Regenerate
              </Button>
            </div>
          </>
        )}
      </CardContent>
      
      <CardFooter className="flex justify-between">
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button 
          onClick={handleApplyTitle}
          disabled={!selectedTitle || isProcessing}
        >
          Apply Selected Title
        </Button>
      </CardFooter>
    </Card>
  );
};

export default TitleGenerator;
